// Bottom Navigation Hook
// Manages bottom navigation state and behavior

import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";

interface UseBottomNavigationReturn {
  isVisible: boolean;
  isSwipeModeActive: boolean;
  activeItem: string;
  setVisibility: (visible: boolean) => void;
  setSwipeModeActive: (active: boolean) => void;
}

export const useBottomNavigation = (): UseBottomNavigationReturn => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [isSwipeModeActive, setIsSwipeModeActive] = useState(false);

  // Get current active item based on route
  const getActiveItem = useCallback((): string => {
    const currentPath = location.pathname;
    
    if (currentPath.startsWith('/social-feed')) return 'home';
    if (currentPath.startsWith('/heart-games')) return 'heart-games';
    if (currentPath.startsWith('/swipe')) return 'swipe';
    if (currentPath.startsWith('/messaging')) return 'messaging';
    if (currentPath.startsWith('/profile/view') || currentPath.startsWith('/profile')) return 'profile';
    if (currentPath.startsWith('/dashboard') || currentPath === '/') return 'home';
    
    return 'home';
  }, [location.pathname]);

  const activeItem = getActiveItem();

  // Auto-hide navigation on certain pages
  useEffect(() => {
    const hideNavPages = [
      '/profile/setup',
      '/login',
      '/registration'
    ];
    
    const shouldHide = hideNavPages.some(page => 
      location.pathname.includes(page)
    ) || isSwipeModeActive;
    
    // Add a small delay to ensure proper timing
    const timer = setTimeout(() => {
      setIsVisible(!shouldHide);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [location.pathname, isSwipeModeActive]);

  // Set visibility - memoized to prevent unnecessary re-renders
  const setVisibility = useCallback((visible: boolean) => {
    setIsVisible(visible);
  }, []);

  // Set swipe mode active - memoized to prevent unnecessary re-renders
  const setSwipeModeActive = useCallback((active: boolean) => {
    setIsSwipeModeActive(active);
  }, []);

  return {
    isVisible,
    isSwipeModeActive,
    activeItem,
    setVisibility,
    setSwipeModeActive,
  };
};
